import { FC, MouseEvent, MouseEventHandler, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import SaveAlt from "@mui/icons-material/SaveAlt";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import Undo from "@mui/icons-material/Undo";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useFetchEmployee } from "src/Pages/Company/Absences/api/employee.queries";
import {
  useAcceptAbsenceRequests,
  useDownloadAbsenceRequestPdf,
  useFetchEmployeeAbsenceRequest,
  useFetchAbsenceRequestHistory,
  useIsAbsenceRequestActionPending,
} from "src/Pages/Company/Absences/api/absenceRequests.queries";
import { useConfirmRejection } from "src/utils/hooks/absences/useConfirmRejection";
import { useRequireCorrection } from "src/utils/hooks/absences/useRequireCorrection";
import { useConfirmCancellation } from "src/utils/hooks/absences/useConfirmCancellation";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { getEtag } from "src/utils/ApiClient/etagCache";
import formatDateRangePreview, {
  formatHourRangePreview,
} from "src/utils/Company/Absences/formatDateRangePreview";
import Title from "src/Pages/Company/Absences/Title/Title";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import { Loader } from "src/Components/Loader/Loader";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import FileList from "src/Pages/Company/Absences/Forms/FileList/FileList";
import { StatusChip } from "src/Pages/Company/Absences/AbsenceRequest/StatusChip";
import EmployeeCardSkeleton from "src/Pages/Company/Absences/EmployeeCard/EmployeeCardSkeleton";
import EmployeeCard from "src/Pages/Company/Absences/EmployeeCard";
import EmployeeDetails from "src/Pages/Company/Absences/EmployeeDetails";
import AbsenceRequestHistory from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestHistory";
import AbsenceRequestCustomFieldsPreview from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestCustomFieldsPreview";
import { EPatternUnit } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import AbsenceRequestCalendarPreview from "./AbsenceRequestCalendarPreview/AbsenceRequestCalendarPreview";
import { Status } from "./AbsenceRequest.models";

enum ActionKey {
  EDIT = "EDIT",
  DOWNLOAD_PDF = "DOWNLOAD_PDF",
  SEND_FOR_CORRECTION = "SEND_FOR_CORRECTION",
  REJECT = "REJECT",
  CANCEL = "CANCEL",
  APPROVE = "APPROVE",
}

const ORDERED_ACTION_KEYS: ActionKey[] = [
  ActionKey.EDIT,
  ActionKey.DOWNLOAD_PDF,
  ActionKey.SEND_FOR_CORRECTION,
  ActionKey.REJECT,
  ActionKey.CANCEL,
  ActionKey.APPROVE,
];

const PRIORITY_ACTION_KEYS: ActionKey[] = [ActionKey.REJECT, ActionKey.APPROVE];

const MAX_VISIBLE_ACTIONS = 2;

const AbsenceRequestPreview: FC = () => {
  const { absenceRequestId } = useParams<Record<string, string>>();
  const basePath = useBasePath();
  const logStatus = useLogStatus();

  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const actionsMenuOpen = Boolean(actionsMenuAnchorEl);
  const closeActionsMenu = () => setActionsMenuAnchorEl(null);

  const eTag = getEtag(`/v1/admin/absence-requests/${absenceRequestId}`) ?? "";

  if (!absenceRequestId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Request ID provided.",
      file: "AbsenceRequestPreview.tsx",
    });
  }

  const {
    data: absenceRequest,
    isFetching: isFetchingAbsenceRequest,
    isError: isErrorAbsenceRequest,
  } = useFetchEmployeeAbsenceRequest(absenceRequestId!);

  const employeeId = useMemo(() => {
    if (!absenceRequest) return undefined;

    return absenceRequest.employee.employeeId;
  }, [absenceRequest]);

  const {
    data: employee,
    isFetching: isFetchingEmployee,
    isError: isErrorEmployee,
  } = useFetchEmployee(employeeId);

  const {
    data: absenceRequestHistory,
    isFetching: isFetchingAbsenceRequestHistory,
    isError: isErrorAbsenceRequestHistory,
  } = useFetchAbsenceRequestHistory(absenceRequestId!);

  const { mutate: acceptAbsenceRequests, isPending: isPendingAcceptMutation } =
    useAcceptAbsenceRequests();

  // Any accept/reject/cancel/correction in flight (from this page or a modal).
  const isActionPending = useIsAbsenceRequestActionPending();

  const {
    mutate: downloadAbsenceRequestPdf,
    isPending: isPendingDownloadPdfMutation,
  } = useDownloadAbsenceRequestPdf();

  const { confirmRejection } = useConfirmRejection();

  const { requireCorrection } = useRequireCorrection();

  const { confirmCancellation } = useConfirmCancellation();

  if (!isFetchingEmployee && isFetchingAbsenceRequest) return <Loader />;

  if (isErrorEmployee || isErrorAbsenceRequest) return <ErrorDataFetching />;

  if (!absenceRequest || !employee) return null;

  const showErrorMessages =
    absenceRequest.status === Status.WAITING_FOR_APPROVAL ||
    absenceRequest.status === Status.CORRECTION_REQUIRED;

  const { actionsAllowed } = absenceRequest;

  const actionsConfig: Record<
    ActionKey,
    {
      label: string;
      icon?: JSX.Element;
      variant: "outlined" | "contained";
      color?: "error";
      to?: string;
      loading?: boolean;
      disabled?: boolean;
      onClick?: MouseEventHandler;
      testId: string;
    }
  > = {
    [ActionKey.EDIT]: {
      label: __("Edytuj"),
      icon: <EditOutlined />,
      variant: "outlined",
      to: `${basePath}/absences/absence-request/${absenceRequestId}/edit/${employeeId}`,
      testId: "absence-request-preview__edit-button",
    },
    [ActionKey.CANCEL]: {
      label: __("Anuluj wniosek"),
      icon: <CancelOutlined />,
      variant: "outlined",
      color: "error",
      disabled: isActionPending,
      onClick: () =>
        confirmCancellation(
          absenceRequestId,
          absenceRequest.absenceType.name,
          eTag
        ),
      testId: "absence-request-preview__cancel-button",
    },
    [ActionKey.SEND_FOR_CORRECTION]: {
      label: __("Zgłoś do poprawy"),
      icon: <Undo />,
      variant: "outlined",
      disabled: isActionPending,
      onClick: () => requireCorrection(absenceRequestId, eTag),
      testId: "absence-request-preview__send-for-correction-button",
    },
    [ActionKey.REJECT]: {
      label: __("Odrzuć wniosek"),
      variant: "outlined",
      color: "error",
      disabled: isActionPending,
      onClick: () =>
        confirmRejection({
          absenceRequestId,
          absenceTypeName: absenceRequest.absenceType.name,
          employeeFirstName: employee.firstName,
          employeeLastName: employee.lastName,
          eTag,
        }),
      testId: "absence-request-preview__reject-button",
    },
    [ActionKey.APPROVE]: {
      label: __("Akceptuj wniosek"),
      variant: "contained",
      loading: isPendingAcceptMutation,
      disabled: isActionPending,
      onClick: () =>
        acceptAbsenceRequests({
          absenceRequests: [absenceRequestId!],
          absenceRequestsWithETags: [
            {
              absenceRequestId,
              eTag,
            },
          ],
        }),
      testId: "absence-request-preview__approve-button",
    },
    [ActionKey.DOWNLOAD_PDF]: {
      label: __("Pobierz wniosek w PDF"),
      icon: <SaveAlt />,
      variant: "outlined",
      loading: isPendingDownloadPdfMutation,
      onClick: () => downloadAbsenceRequestPdf(absenceRequestId!),
      testId: "absence-request-preview__pdf-download-button",
    },
  };

  const actionAllowedMap: Record<ActionKey, boolean> = {
    [ActionKey.EDIT]: actionsAllowed.editable,
    [ActionKey.CANCEL]: actionsAllowed.cancelable,
    [ActionKey.SEND_FOR_CORRECTION]: actionsAllowed.sendForCorrectionEligible,
    [ActionKey.REJECT]: actionsAllowed.rejectable,
    [ActionKey.APPROVE]: actionsAllowed.approvable,
    [ActionKey.DOWNLOAD_PDF]: true,
  };

  const allowedActionKeys = ORDERED_ACTION_KEYS.filter(
    (key) => actionAllowedMap[key]
  );

  const priorityAllowedKeys = PRIORITY_ACTION_KEYS.filter(
    (key) => actionAllowedMap[key]
  );

  let visibleActionKeys: ActionKey[];

  if (priorityAllowedKeys.length === 2) {
    // Both "Akceptuj wniosek" and "Odrzuć wniosek" are allowed - show only them.
    visibleActionKeys = priorityAllowedKeys;
  } else if (priorityAllowedKeys.length === 1) {
    // Only one of them is allowed - show it plus the next available action.
    const otherAllowedKey = allowedActionKeys.find(
      (key) => !priorityAllowedKeys.includes(key)
    );

    visibleActionKeys = otherAllowedKey
      ? [...priorityAllowedKeys, otherAllowedKey]
      : priorityAllowedKeys;
  } else {
    // Neither priority action is allowed - fall back to the first available actions.
    visibleActionKeys = allowedActionKeys.slice(0, MAX_VISIBLE_ACTIONS);
  }

  const hiddenActionKeys = allowedActionKeys.filter(
    (key) => !visibleActionKeys.includes(key)
  );

  const visibleActions = ORDERED_ACTION_KEYS.filter((key) =>
    visibleActionKeys.includes(key)
  ).map((key) => ({ key, ...actionsConfig[key] }));

  const hiddenActions = ORDERED_ACTION_KEYS.filter((key) =>
    hiddenActionKeys.includes(key)
  ).map((key) => ({ key, ...actionsConfig[key] }));

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Kartoteki pracowników"),
            to: `${basePath}/absences/absence-records`,
          },
          {
            label: __("Lista wniosków"),
            to: `${basePath}/absences/employee/${employeeId}#requests`,
          },
          {
            label: __(
              `${__("Podgląd wniosku")} ${absenceRequest.absenceType.name}`
            ),
          },
        ]}
      />
      <Title
        title={`${__("Podgląd wniosku")} ${absenceRequest.absenceType.name}`}
      />
      {isFetchingEmployee ? (
        <EmployeeCardSkeleton />
      ) : (
        <EmployeeCard data={employee} />
      )}
      <Paper
        sx={{ p: "2rem", mt: "1rem" }}
        data-testid="absence-request-preview"
      >
        <Stack direction="row" spacing={2} sx={{ justifyContent: "end" }}>
          {visibleActions.map((action) =>
            action.to ? (
              <Button
                key={action.key}
                variant={action.variant}
                color={action.color}
                size="large"
                startIcon={action.icon}
                component={Link}
                to={action.to}
                data-testid={action.testId}
              >
                {action.label}
              </Button>
            ) : (
              <Button
                key={action.key}
                variant={action.variant}
                color={action.color}
                size="large"
                startIcon={action.icon}
                onClick={action.onClick}
                loading={action.loading}
                disabled={action.disabled}
                data-testid={action.testId}
              >
                {action.label}
              </Button>
            )
          )}
          {!!hiddenActions.length && (
            <>
              <Button
                variant="outlined"
                size="large"
                endIcon={<ArrowDropDown />}
                onClick={(event: MouseEvent<HTMLButtonElement>) =>
                  setActionsMenuAnchorEl(event.currentTarget)
                }
                data-testid="absence-request-preview__other-actions-button"
              >
                {__("Pozostałe akcje")}
              </Button>
              <Menu
                anchorEl={actionsMenuAnchorEl}
                open={actionsMenuOpen}
                onClose={closeActionsMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                data-testid="absence-request-preview__other-actions-menu"
              >
                {hiddenActions.map((action) =>
                  action.to ? (
                    <MenuItem
                      key={action.key}
                      component={Link}
                      to={action.to}
                      onClick={closeActionsMenu}
                      data-testid={action.testId}
                    >
                      <ListItemIcon>{action.icon}</ListItemIcon>
                      <ListItemText>{action.label}</ListItemText>
                    </MenuItem>
                  ) : (
                    <MenuItem
                      key={action.key}
                      disabled={action.disabled}
                      onClick={(event: MouseEvent<HTMLLIElement>) => {
                        closeActionsMenu();
                        action.onClick?.(event);
                      }}
                      data-testid={action.testId}
                    >
                      <ListItemIcon>{action.icon}</ListItemIcon>
                      <ListItemText>{action.label}</ListItemText>
                    </MenuItem>
                  )
                )}
              </Menu>
            </>
          )}
        </Stack>
        {absenceRequest?.status === Status.CORRECTION_REQUIRED &&
          absenceRequest.note && (
            <Alert severity="warning" sx={{ mb: "1.5rem" }}>
              <AlertTitle>{__("Wymagane zmiany na wniosku")}:</AlertTitle>
              {absenceRequest.note.content}
            </Alert>
          )}
        <Fieldset
          legend={__("Status")}
          divider
          data-testid="absence-request-preview__status"
        >
          <StatusChip variant={absenceRequest.status} />
        </Fieldset>
        <>
          <Typography variant="body1" component="span">
            {__("Data")}:
          </Typography>{" "}
          <Typography
            variant="subtitle1"
            component="span"
            data-testid="absence-request-preview__times"
          >
            {absenceRequest.type === EPatternUnit.Day
              ? formatDateRangePreview(absenceRequest.absenceTimes)
              : formatHourRangePreview(absenceRequest.absenceTimes)}
          </Typography>
          <br />
          <Typography variant="body1" component="span">
            {__("Tryb")}:
          </Typography>{" "}
          <Typography variant="subtitle1" component="span">
            {absenceRequest.type === EPatternUnit.Hour
              ? __("godzinowy")
              : __("dzienny")}
          </Typography>
          {absenceRequest.type === EPatternUnit.Day && (
            <>
              <Typography
                variant="body1"
                data-testid="absence-request-preview__total-day-count"
              >
                {__("Dni kalendarzowe")}:{" "}
                <strong>{absenceRequest.totalTimeAmount.days}</strong>
              </Typography>
              <Typography
                variant="body1"
                data-testid="absence-request-preview__effective-day-count"
              >
                {__("Dni robocze")}:{" "}
                <strong>{absenceRequest.effectiveTimeAmount.days}</strong>
              </Typography>
            </>
          )}
          <Typography
            variant="body1"
            data-testid="absence-request-preview__hour-count"
          >
            {__("Liczba godzin")}:{" "}
            <strong>{absenceRequest.effectiveTimeAmount.hours}</strong>
          </Typography>
        </>
        <Divider />
        <Fieldset
          legend={__("Kalendarz")}
          divider
          data-testid="absence-request-preview__calendar"
        >
          <AbsenceRequestCalendarPreview employeeId={employeeId!} />
        </Fieldset>
        <Stack direction="row" spacing={2}>
          <Box sx={{ minWidth: "376px" }}>
            <Typography variant="subtitle1">
              {__("Osoba akceptująca wniosek")}:
            </Typography>
            <Typography data-testid="absence-request-preview__approver">
              {absenceRequest.approver ? (
                <EmployeeDetails
                  data={absenceRequest.approver}
                  showFk
                  error={
                    // eslint-disable-next-line no-nested-ternary
                    showErrorMessages
                      ? absenceRequest.approver.isActive === false
                        ? "Wskazany akceptant jest nieaktywny. Wybierz inną osobę akceptującą."
                        : undefined
                      : undefined
                  }
                />
              ) : (
                "-"
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1">
              {__("Osoba zastępująca")}:
            </Typography>
            <Typography data-testid="absence-request-preview__replacer">
              {absenceRequest.replacer ? (
                <EmployeeDetails
                  data={absenceRequest.replacer}
                  showFk
                  error={
                    // eslint-disable-next-line no-nested-ternary
                    showErrorMessages
                      ? // eslint-disable-next-line no-nested-ternary
                        absenceRequest.replacer.activeAtRequestTimeRange ===
                        false
                        ? __(
                            "Wskazana osoba jest nieaktywna w terminie składanego wniosku. Wybierz inną osobę na zastępstwo."
                          )
                        : absenceRequest.replacer.presentAtRequestTimeRange ===
                          false
                        ? __(
                            "Wskazana osoba jest nieobecna w terminie składanego wniosku. Wybierz inną osobę na zastępstwo."
                          )
                        : undefined
                      : undefined
                  }
                />
              ) : (
                "-"
              )}
            </Typography>
          </Box>
        </Stack>
        <Divider />
        {absenceRequest.justification && (
          <Fieldset legend={__("Uzasadnienie")}>
            <Typography
              variant="body1"
              data-testid="absence-request-preview__justification"
            >
              {absenceRequest.justification}
            </Typography>
          </Fieldset>
        )}
        {absenceRequest.absenceType.customFieldsConfiguration && (
          <AbsenceRequestCustomFieldsPreview
            customFieldsConfiguration={
              absenceRequest.absenceType.customFieldsConfiguration
            }
            customFieldValues={absenceRequest.customFieldValues ?? {}}
          />
        )}
        {absenceRequest.comment && (
          <Fieldset legend={__("Komentarz")}>
            <Typography
              variant="body1"
              data-testid="absence-request-preview__comment"
            >
              {absenceRequest.comment}
            </Typography>
          </Fieldset>
        )}
        {absenceRequest.attachments?.length ? (
          <FileList
            scopeId={absenceRequestId!}
            title={__("Załączniki")}
            data={absenceRequest.attachments}
            downloadLabel={__("Pobierz")}
            data-testid="absence-request-preview__attachments"
          />
        ) : null}
      </Paper>
      <Paper
        sx={{ padding: "1rem", mt: "2rem" }}
        data-testid="absence-request-history"
      >
        {/* eslint-disable-next-line no-nested-ternary */}
        {isErrorAbsenceRequestHistory ? (
          <Alert severity="error">
            {__("Błąd pobierania danych historii wniosku.")}
          </Alert>
        ) : isFetchingAbsenceRequestHistory ? (
          <Loader />
        ) : (
          absenceRequestHistory && (
            <AbsenceRequestHistory
              data={absenceRequestHistory}
              billingCycle={absenceRequest.type}
            />
          )
        )}
      </Paper>
    </>
  );
};

export default AbsenceRequestPreview;
